import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  Divider,
  Grid,
  makeStyles,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  withStyles,
} from '@material-ui/core';
import { TabContext, TabList, TabPanel } from '@material-ui/lab';
import { useLocation } from 'react-router';
import NoPaperPageContent from 'components/Generic/NoPaperPageContent';
import { useClientById } from 'hooks';
import Loading from 'components/Loading';
import { Routes } from 'routes';
import { colors, fonts } from 'theme';
import { Client, Contact } from 'types';
import SortingCenterContractList from './SortingCenterContractList';
import ClientBuyerContracts from './ClientBuyerContracts';

const useStyles = makeStyles({
  label: {
    fontWeight: 600,
    marginRight: '16px',

    '&:after': {
      content: '" :"',
    },
  },
  cardLabel: {
    fontWeight: 600,
    marginRight: '16px',
    width: '140px',
  },
  clientShow: {
    maxWidth: '77%',
  },
  marginTopDivider: {
    marginTop: '3em',
  },
  clientCard: {
    padding: '39px',
    minHeight: '300px',
  },
  cardTitle: {
    fontSize: '24px',
    paddingBottom: '20px',
    color: colors.primary,
  },
  tab: {
    justifyContent: 'start',
    alignContent: 'start',
    alignItems: 'left',
    padding: '0',
  },
});

const StyledTableCell = withStyles({
  root: {
    fontFamily: fonts.secondary,
    fontSize: '12',
    border: 'none',
    padding: '2px 0',
  },
})(TableCell);

interface Line {
  key: string;
  value: string | number;
}

interface ClientCardProps {
  title: string;
  lines: Line[];
}

const ClientCard: React.FC<ClientCardProps> = ({ title, lines }: ClientCardProps) => {
  const classes = useStyles();

  return (
    <Card className={classes.clientCard}>
      <Typography variant="h2" className={classes.cardTitle}>
        {title}
      </Typography>
      <Table>
        <TableBody>
          {lines.map((line, index) => (
            <TableRow key={`${title}_table_${index}`}>
              <StyledTableCell width="50%">
                <span className={classes.cardLabel}>{line.key}</span>
              </StyledTableCell>
              <StyledTableCell width="50%">{`: ${line.value}`}</StyledTableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

export const TAB_PANEL_PARAM = 'tabPanelValue';
enum TabLabels {
  Information = 'information',
  Contracts = 'contracts',
  Buyers = 'buyers',
}

const formatClientProperty = (client: Client | null | undefined, property: keyof Client) => {
  return client && client[property] ? `${client[property]}` : '-';
};

const fomatContactProperty = (client: Client | null | undefined, property: keyof Contact) => {
  return client && client.contact && client.contact[property] ? `${client.contact[property]}` : '-';
};

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}
const ClientShow: React.FC = () => {
  const classes = useStyles();

  const { clientId } = useParams<{ clientId: string }>();
  const { t } = useTranslation();
  const { t: pt } = useTranslation('translation', { keyPrefix: 'clients' });
  const { value: client, loading } = useClientById(clientId);

  const tabPanelValue = useQuery()?.get(TAB_PANEL_PARAM);

  const [selectedTab, setSelectedTab] = useState(tabPanelValue ?? TabLabels.Information);

  const clientCardLines = [
    {
      key: pt('shortShortName'),
      value: formatClientProperty(client, 'shortName'),
    },
    {
      key: pt('fullName'),
      value: formatClientProperty(client, 'fullName'),
    },
    {
      key: pt('addressLine'),
      value: formatClientProperty(client, 'addressLine'),
    },
    {
      key: pt('postalCode'),
      value: formatClientProperty(client, 'postalCode'),
    },
    {
      key: pt('cityName'),
      value: formatClientProperty(client, 'city'),
    },
    {
      key: pt('phone'),
      value: formatClientProperty(client, 'phoneNumber'),
    },
    {
      key: pt('inhabitants'),
      value: formatClientProperty(client, 'inhabitants'),
    },
    {
      key: pt('code'),
      value: formatClientProperty(client, 'code'),
    },
  ];

  const contactCardList = [
    {
      key: t('contacts.lastName'),
      value: fomatContactProperty(client, 'lastName'),
    },
    {
      key: t('contacts.firstName'),
      value: fomatContactProperty(client, 'firstName'),
    },
    {
      key: t('contacts.email'),
      value: fomatContactProperty(client, 'email'),
    },
    {
      key: t('contacts.phone'),
      value: fomatContactProperty(client, 'phoneNumber'),
    },
  ];

  return (
    <>
      <NoPaperPageContent title={t('clients.show.title')} backRoute={Routes.Clients}>
        {loading ? (
          <Loading />
        ) : (
          client && (
            <Box className={classes.clientShow}>
              <Divider className={classes.marginTopDivider} />
              <Box display="flex" paddingTop="26px" paddingBottom="26px">
                <Box marginRight="200px">
                  <Typography>
                    <span className={classes.label}>{pt('client')}</span>
                    {client.shortName}
                  </Typography>
                </Box>
                <Typography>
                  <span className={classes.label}>{pt('cityName')}</span>
                  {client.city}
                </Typography>
              </Box>
              <Divider />
              <Box display="flex" paddingTop="26px" paddingBottom="52px" marginRight="200px">
                <Typography>
                  <span className={classes.label}>{pt('code')}</span>
                  {client.code ? client.code : '-'}
                </Typography>
              </Box>
              <TabContext value={selectedTab}>
                <TabList
                  value={selectedTab}
                  onChange={(e, newValue) => {
                    setSelectedTab(newValue);
                  }}
                  indicatorColor="primary"
                  textColor="primary"
                >
                  <Tab label={pt('show.tabs.information')} value={TabLabels.Information} />
                  <Tab label={pt('show.tabs.contracts')} value={TabLabels.Contracts} />
                  <Tab label={pt('show.tabs.buyers')} value={TabLabels.Buyers} />
                </TabList>
                <Divider />
                <TabPanel value={TabLabels.Information}>
                  <Grid container spacing={3}>
                    <Grid item xs={6}>
                      <ClientCard title={pt('client')} lines={clientCardLines} />
                    </Grid>
                    <Grid item xs={6}>
                      <ClientCard title={pt('contact')} lines={contactCardList} />
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={TabLabels.Contracts}>
                  <SortingCenterContractList />
                </TabPanel>
                <TabPanel value={TabLabels.Buyers} className={classes.tab}>
                  <ClientBuyerContracts />
                </TabPanel>
              </TabContext>
            </Box>
          )
        )}
      </NoPaperPageContent>
    </>
  );
};
export default ClientShow;
